import { listClosingReasons } from '@/features/atendimento/api';
import { ActionIconButton } from '@/shared/components/ui/ActionIconButton';
import { Button } from '@/shared/components/ui/Button';
import { ToggleCard } from '@/shared/components/ui/Switch';
import { useClientPagination } from '@/shared/hooks/useClientPagination';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { confirm } from '@/shared/lib/confirm';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Loader2, Pencil, Plus, RotateCcw, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import {
  type ClosingReasonRow,
  createClosingReason,
  deleteClosingReason,
  updateClosingReason,
} from '../api';

export function ClosingReasonsTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['closing-reasons'], queryFn: listClosingReasons });
  const [editing, setEditing] = useState<ClosingReasonRow | 'new' | null>(null);
  // Motivo do sistema ("Não especificado") fica fora desta tela: ele existe em
  // toda conta, é o que o fechamento sem motivo grava, e não se edita nem apaga.
  const all = (q.data ?? []).filter((r) => !r.systemKey);
  const { visible, hasMore, sentinelRef } = useClientPagination(all);

  const delMut = useMutation({
    mutationFn: deleteClosingReason,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['closing-reasons'] }),
  });
  const toggleMut = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      updateClosingReason(id, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['closing-reasons'] }),
  });

  return (
    <section className="flex h-full flex-col overflow-hidden">
      <header className="mb-5 flex flex-shrink-0 items-center justify-between">
        <div>
          <h2
            className="font-bold text-[20px] text-n-900"
            style={{ letterSpacing: '-.02em', marginBottom: '4px' }}
          >
            Motivos de fechamento
          </h2>
          <p className="text-[13px] text-n-500">Opções que aparecem ao fechar um ticket.</p>
        </div>
        <Button onClick={() => setEditing('new')}>
          <Plus aria-hidden="true" />
          Novo motivo
        </Button>
      </header>

      {!q.isLoading && all.length > 0 && (
        <p className="mb-2 flex-shrink-0 text-[12px] text-n-500">
          {visible.length === all.length
            ? `${all.length} ${all.length === 1 ? 'motivo' : 'motivos'}`
            : `Mostrando ${visible.length} de ${all.length} motivos`}
        </p>
      )}
      <div className="min-h-0 flex flex-col overflow-y-auto rounded-md border border-n-200 bg-n-0 shadow-sm">
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 z-10 border-b border-n-200 bg-n-50 text-left">
            <tr>
              <th className="px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Nome
              </th>
              <th className="w-[120px] px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Status
              </th>
              <th className="px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Mensagem de fechamento
              </th>
              <th className="w-[200px] px-[14px] py-[10px] text-right text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr>
                <td colSpan={4} className="px-3 py-8 text-center">
                  <Loader2 size={18} className="mx-auto animate-spin text-n-400" />
                </td>
              </tr>
            )}
            {!q.isLoading && all.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-12 text-center text-n-500">
                  Nenhum motivo cadastrado.
                </td>
              </tr>
            )}
            {visible.map((r) => (
              <tr key={r.id} className="border-b border-n-100 last:border-b-0 hover:bg-n-50">
                <td className="px-[14px] py-[11px] font-medium text-n-900">{r.name}</td>
                <td className="px-[14px] py-[11px]">
                  <span
                    className={
                      r.active
                        ? 'rounded-pill bg-success-50 px-2 py-0.5 text-[11px] font-semibold text-success-700'
                        : 'rounded-pill bg-n-100 px-2 py-0.5 text-[11px] font-semibold text-n-600'
                    }
                  >
                    {r.active ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td
                  className="max-w-0 truncate px-[14px] py-[11px] text-n-600"
                  title={r.closingMessage ?? undefined}
                >
                  {r.closingMessage ? r.closingMessage : <span className="text-n-300">—</span>}
                </td>
                <td className="px-[14px] py-[11px]">
                  <div className="flex justify-end gap-1">
                    <ActionIconButton
                      icon={Pencil}
                      label="Editar"
                      tone="brand"
                      onClick={() => setEditing(r)}
                    />
                    <ActionIconButton
                      icon={r.active ? Ban : RotateCcw}
                      label={r.active ? 'Inativar' : 'Reativar'}
                      tone="warning"
                      onClick={() => toggleMut.mutate({ id: r.id, active: !r.active })}
                    />
                    <ActionIconButton
                      icon={Trash2}
                      label="Apagar"
                      tone="danger"
                      onClick={async () => {
                        const ok = await confirm({
                          title: `Apagar "${r.name}"?`,
                          confirmText: 'Apagar',
                          variant: 'danger',
                        });
                        if (ok) delMut.mutate(r.id);
                      }}
                    />
                  </div>
                </td>
              </tr>
            ))}
            {hasMore && (
              <tr ref={sentinelRef}>
                <td colSpan={4} className="py-4 text-center text-[12px] text-n-500">
                  <span className="inline-flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> Carregando mais…
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <ReasonFormModal
          reason={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}

function ReasonFormModal({
  reason,
  onClose,
}: {
  reason: ClosingReasonRow | null;
  onClose: () => void;
}) {
  useEscapeClose(true, onClose);
  const qc = useQueryClient();
  const [name, setName] = useState(reason?.name ?? '');
  const [active, setActive] = useState(reason?.active ?? true);
  const [closingMessage, setClosingMessage] = useState(reason?.closingMessage ?? '');
  const [sendRating, setSendRating] = useState(reason?.sendRating ?? false);
  const mut = useMutation({
    mutationFn: async () => {
      // Vazio (só espaços) → null = não envia mensagem ao fechar.
      const cm = closingMessage.trim().length > 0 ? closingMessage : null;
      if (reason)
        await updateClosingReason(reason.id, { name, active, closingMessage: cm, sendRating });
      else await createClosingReason({ name, active, closingMessage: cm, sendRating });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['closing-reasons'] });
      onClose();
    },
  });
  return (
    // Clicar fora NÃO fecha (dono, 27/out/2026) — só o X, Cancelar ou Esc.
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      {/* Mesma largura do cadastro de mensagens rápidas (dono, 27/out/2026). */}
      <div className="w-[520px] max-w-[95vw] rounded-lg bg-n-0 p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-[16px]">{reason ? 'Editar motivo' : 'Novo motivo'}</h3>
          <button type="button" onClick={onClose} className="text-n-500" aria-label="Fechar">
            <X size={16} />
          </button>
        </div>
        <div className="mt-4 flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-[12px] text-n-700">Nome</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded-md border border-n-200 px-3 py-2 text-[13px]"
              autoFocus
            />
          </label>
          {/* Chaves empilhadas com título em cima, igual ao Editar usuário
              (dono, 27/out/2026), ANTES da mensagem de fechamento. */}
          <div className="flex flex-col gap-1.5">
            <span className="font-semibold text-[12px] text-n-700">Opções</span>
            <div className="flex flex-col gap-2">
              <ToggleCard
                labelSize="sm"
                label="Motivo ativo"
                checked={active}
                onChange={setActive}
              />
              <ToggleCard
                labelSize="sm"
                label="Enviar avaliação ao encerrar"
                checked={sendRating}
                onChange={setSendRating}
              />
            </div>
          </div>
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-[12px] text-n-700">Mensagem de fechamento</span>
            <textarea
              value={closingMessage}
              onChange={(e) => setClosingMessage(e.target.value)}
              rows={4}
              placeholder="digite aqui..."
              className="resize-none rounded-md border border-n-200 px-3 py-2 text-[13px]"
            />
            <span className="text-[11px] text-n-500">
              Mensagem enviada ao encerrar o atendimento. Deixe em branco para não enviar. Aceita
              variáveis (ex: <code className="text-n-600">{'{{firstname}}'}</code>,{' '}
              <code className="text-n-600">{'{{name}}'}</code>,{' '}
              <code className="text-n-600">{'{{atendente}}'}</code>).
            </span>
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || !name.trim()}>
            {mut.isPending && <Loader2 size={12} className="animate-spin" />}
            Salvar
          </Button>
        </div>
      </div>
    </div>
  );
}
